import { supabase, invokeEdgeFunction, throwOnError } from "@/lib/supabase-client";
import type { OrganizerEvent } from "@/types/events";
import type { LiveTournamentState } from "@/utils/liveTournament/types";
import type { SetupDraft } from "@/components/liveTournament/types";
import { normalizeCommunityCupDivisionData } from "@/lib/event-state";

// ─── Organizer Events ─────────────────────────────────────────────────────────

// Runs the same legacy-shape Community Cup migration `sanitizeEvent()`
// (src/context/OrganizerEventsContext.tsx) applies to every event hydrated
// through the organizer's own local/cloud snapshot. Read paths that bypass
// that context entirely — public/anonymous visitors and captains — used to
// return `row.state` raw, so a pre-existing single-division Community Cup
// event (stored before the multi-division migration) would still have
// `divisions[id].communityCup === undefined` here, even though the same row
// normalizes fine once loaded through the organizer's own workspace. Final
// review C2.
export const hydrateOrganizerEvent = (event: OrganizerEvent): OrganizerEvent =>
  normalizeCommunityCupDivisionData(event) as OrganizerEvent;

export const fetchEventBySlug = async (slug: string): Promise<OrganizerEvent | null> => {
  // Try direct read first — works when authenticated as owner or accepted co-host.
  const { data } = await supabase
    .from("organizer_events")
    .select("id, slug, state")
    .eq("slug", slug)
    .maybeSingle();
  if (data) {
    return hydrateOrganizerEvent({ ...(data.state as OrganizerEvent), id: data.id, slug: data.slug });
  }
  // RLS blocks anonymous and non-collaborator reads; fall back to SECURITY DEFINER RPC.
  const { data: rpcData, error: rpcError } = await supabase.rpc("get_public_event_by_slug", {
    p_slug: slug,
  });
  if (rpcError) {
    console.warn("[organizer-sync] fetchEventBySlug rpc error", rpcError);
    return null;
  }
  return rpcData ? hydrateOrganizerEvent(rpcData as OrganizerEvent) : null;
};

export const loadEventsFromCloud = async (): Promise<OrganizerEvent[]> => {
  const { data, error } = await supabase
    .from("organizer_events")
    .select("state")
    .order("updated_at", { ascending: false });

  if (error) {
    console.warn("[organizer-sync] loadEventsFromCloud error", error);
    return [];
  }

  return (data ?? [])
    .map((row) => row.state as OrganizerEvent)
    .filter(Boolean);
};

// Fetches a single event by slug via a SECURITY DEFINER RPC so anonymous and
// non-collaborator visitors can render /events/:slug. RLS on organizer_events
// otherwise restricts SELECT to the owner and accepted co-hosts.
export const loadPublicEventBySlug = async (
  slug: string
): Promise<OrganizerEvent | null> => {
  const { data, error } = await supabase.rpc("get_public_event_by_slug", {
    p_slug: slug,
  });

  if (error) {
    console.warn("[organizer-sync] loadPublicEventBySlug error", error);
    return null;
  }

  return data ? hydrateOrganizerEvent(data as OrganizerEvent) : null;
};

/** Id of the event that holds a public link ending, or null when it's free. Throws if the check fails. */
export const slugHolderId = async (slug: string): Promise<string | null> => {
  const { data, error } = await supabase.rpc("get_public_event_by_slug", { p_slug: slug });
  if (error) throwOnError(error, "Unable to check the link");
  return data ? ((data as { id?: string }).id ?? null) : null;
};

export interface PublicLiveState {
  eventName: string;
  venue: string | null;
  courtNames?: string[] | null;
  live: Partial<LiveTournamentState> | null;
}

// TV page read: null when the slug is unknown, the shared display is off, or no live state exists yet.
export const loadPublicLiveStateBySlug = async (slug: string): Promise<PublicLiveState | null> => {
  const { data, error } = await supabase.rpc("get_public_live_state_by_slug", { p_slug: slug });
  if (error) throw error;
  return (data as PublicLiveState | null) ?? null;
};

/** One event as saved in the cloud right now (signed-in owner or collaborator), or null. */
export const fetchEventById = async (id: string): Promise<OrganizerEvent | null> => {
  const { data, error } = await supabase.from("organizer_events").select("id, slug, state").eq("id", id).maybeSingle();
  if (error || !data) return null;
  return hydrateOrganizerEvent({ ...(data.state as OrganizerEvent), id: data.id, slug: data.slug });
};

// Bracket TV page read: null when the slug is not unique, the event is not bracket-style, or the display is off.
export const loadPublicBracketDisplayBySlug = async (slug: string): Promise<OrganizerEvent | null> => {
  const { data, error } = await supabase.rpc("get_public_bracket_display_by_slug", { p_slug: slug });
  if (error) throw error;
  return data ? hydrateOrganizerEvent(data as OrganizerEvent) : null;
};

// Returns whether the write actually reached the cloud. Callers that queue
// pending writes (OrganizerEventsContext's debounced sync) use this to decide
// whether to retry on a later cycle instead of silently dropping the write.
export const upsertEventToCloud = async (event: OrganizerEvent, userId: string): Promise<boolean> => {
  const { error } = await supabase.from("organizer_events").upsert(
    {
      id: event.id,
      user_id: userId,
      event_name: event.eventName,
      slug: event.slug,
      format: event.format,
      status: event.status,
      workspace_mode: event.workspaceMode,
      start_date: event.startDate ?? null,
      end_date: event.endDate ?? null,
      community_id: event.communityId ?? null,
      state: event,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" }
  );

  if (error) {
    console.warn("[organizer-sync] upsertEventToCloud error", error);
    return false;
  }
  return true;
};

// See upsertEventToCloud — same success/failure signal for retry purposes.
export const deleteEventFromCloud = async (eventId: string): Promise<boolean> => {
  const { error } = await supabase
    .from("organizer_events")
    .delete()
    .eq("id", eventId);

  if (error) {
    console.warn("[organizer-sync] deleteEventFromCloud error", error);
    return false;
  }
  return true;
};

// ─── Live Tournament States ───────────────────────────────────────────────────

export const loadLiveStateFromCloud = async (
  storageKey: string
): Promise<{ state: LiveTournamentState; updatedAt: string } | null> => {
  const { data, error } = await supabase
    .from("organizer_live_states")
    .select("state, updated_at")
    .eq("storage_key", storageKey)
    .maybeSingle();

  if (error) {
    console.warn("[organizer-sync] loadLiveStateFromCloud error", error);
    return null;
  }

  if (!data?.state) return null;
  return {
    state: data.state as LiveTournamentState,
    updatedAt: (data.updated_at as string | null) ?? new Date().toISOString(),
  };
};

export const upsertLiveStateToCloud = async (
  storageKey: string,
  eventId: string,
  userId: string,
  state: LiveTournamentState,
  updatedAt?: string
): Promise<void> => {
  const { error } = await supabase.from("organizer_live_states").upsert(
    {
      storage_key: storageKey,
      event_id: eventId,
      user_id: userId,
      state,
      updated_at: updatedAt ?? new Date().toISOString(),
    },
    { onConflict: "storage_key" }
  );

  if (error) {
    console.warn("[organizer-sync] upsertLiveStateToCloud error", error);
  }
};

// ─── Setup Drafts ─────────────────────────────────────────────────────────────

export const upsertSetupDraftToCloud = async (
  storageKey: string,
  eventId: string,
  userId: string,
  draft: SetupDraft
): Promise<void> => {
  const { error } = await supabase.from("organizer_setup_drafts").upsert(
    {
      storage_key: storageKey,
      event_id: eventId,
      user_id: userId,
      draft,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "storage_key" }
  );

  if (error) {
    console.warn("[organizer-sync] upsertSetupDraftToCloud error", error);
  }
};

// ─── Event Collaborators ──────────────────────────────────────────────────────

export interface CollaboratorRow {
  id: string;
  event_id: string;
  invite_email: string;
  user_id: string | null;
  role: string;
  community_side: "A" | "B" | null;
  invited_by: string;
  created_at: string;
  accepted_at: string | null;
}

export interface CollaboratorInviteRow extends CollaboratorRow {
  event_name: string;
}

export type CollaboratorInviteDelivery = "sent" | "recorded_without_email";

const normalizeInviteEmail = (email: string) => email.toLowerCase().trim();

export const loadCollaboratorsForEvent = async (
  eventId: string
): Promise<CollaboratorRow[]> => {
  const { data, error } = await supabase
    .from("event_collaborators")
    .select("*")
    .eq("event_id", eventId);

  if (error) {
    console.warn("[organizer-sync] loadCollaboratorsForEvent error", error);
    return [];
  }

  return (data ?? []) as CollaboratorRow[];
};

const sendCollaboratorInviteEmail = async (
  inviteEmail: string,
  eventId?: string,
  eventName?: string,
): Promise<string | null> => {
  try {
    await invokeEdgeFunction("send-collaborator-invite", {
      invite_email: inviteEmail,
      event_id: eventId,
      event_name: eventName ?? "an event",
    });
    return null;
  } catch (err) {
    console.error("[organizer-sync] send-collaborator-invite error:", err);
    return err instanceof Error ? err.message : String(err);
  }
};

export const inviteCollaborator = async (
  eventId: string,
  inviteEmail: string,
  invitedBy: string,
  eventName?: string,
  options?: { community_side?: "A" | "B" },
): Promise<{ error: string | null; inviteDelivery: CollaboratorInviteDelivery | null }> => {
  // A captain invite scopes access to one community_side via the
  // side-scoped RPCs (captain_update_division_communitycup,
  // save_community_cup_roster_preparation); a co-host invite is full
  // event access, gated by is_event_cohost() (events_update RLS policy) -
  // see 20260812152526_community_cup_cohost_writes.sql. This distinction
  // used to be cosmetic (role was read by zero policies); it is now
  // load-bearing.
  const { error } = await supabase.from("event_collaborators").insert({
    event_id: eventId,
    invite_email: normalizeInviteEmail(inviteEmail),
    invited_by: invitedBy,
    role: options?.community_side ? "captain" : "co-host",
    community_side: options?.community_side ?? null,
  });

  if (error) {
    if (error.code === "23505") {
      return { error: "This person has already been invited to this event.", inviteDelivery: null };
    }
    // 42501 = insufficient_privilege, Postgres's code for a WITH CHECK
    // failure. The event_collaborators insert policy requires the caller to
    // own the event row (organizer_events.user_id = auth.uid()) - an
    // accepted co-host is not enough, even though co-hosts can otherwise
    // edit everything on the event. See 20260812152526_community_cup_cohost_writes.sql.
    if (error.code === "42501") {
      return { error: "Only the event owner can invite collaborators.", inviteDelivery: null };
    }
    return { error: error.message, inviteDelivery: null };
  }

  // Only pass eventId for captain invites (community_side set) so the email links to
  // /captain/:eventId. Regular co-host invites get no eventId and land on /organizer.
  const captainEventId = options?.community_side ? eventId : undefined;
  const emailError = await sendCollaboratorInviteEmail(normalizeInviteEmail(inviteEmail), captainEventId, eventName);
  if (emailError) {
    console.error("[organizer-sync] Invite email failed:", emailError);
    return { error: null, inviteDelivery: "recorded_without_email" };
  }

  return { error: null, inviteDelivery: "sent" };
};

export const resendCollaboratorInvite = async (
  inviteEmail: string,
  eventId?: string,
  eventName?: string,
): Promise<{ error: string | null }> => {
  const emailError = await sendCollaboratorInviteEmail(normalizeInviteEmail(inviteEmail), eventId, eventName);
  return { error: emailError };
};

/**
 * Organizer-side removal of a co-host or captain (active or still-pending).
 * `event_collaborators`'s UNIQUE(event_id, invite_email) means one email can
 * only hold ONE role per event - re-inviting someone under a different role
 * or scope requires removing their existing row first. RLS's
 * "collaborators_delete" policy already permits this for whoever sent the
 * invite (invited_by = auth.uid()), which - since only the event owner can
 * ever create a row in the first place - is always the owner in practice.
 */
export const removeCollaborator = async (inviteId: string): Promise<{ error: string | null }> => {
  const { error } = await supabase.from("event_collaborators").delete().eq("id", inviteId);
  return { error: error ? error.message : null };
};

// Co-host rows have community_side === null and role === "co-host" (see
// 20260812152526_community_cup_cohost_writes.sql); captain rows always have
// a non-null community_side. One query covers both identities.
export const fetchMyCollaboratorAccess = async (
  eventId: string,
): Promise<{ communitySide: "A" | "B" | null; isCohost: boolean }> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { communitySide: null, isCohost: false };

  const { data, error } = await supabase
    .from("event_collaborators")
    .select("community_side, role")
    .eq("event_id", eventId)
    .eq("user_id", session.user.id)
    .not("accepted_at", "is", null)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[organizer-sync] fetchMyCollaboratorAccess error:", error);
    return { communitySide: null, isCohost: false };
  }
  return {
    communitySide: (data?.community_side as "A" | "B") ?? null,
    isCohost: data?.role === "co-host",
  };
};

export const loadPendingInvitesForUser = async (
  userEmail: string
): Promise<CollaboratorInviteRow[]> => {
  const { data, error } = await supabase
    .from("event_collaborators")
    .select("*, organizer_events(event_name)")
    .eq("invite_email", normalizeInviteEmail(userEmail))
    .is("accepted_at", null);

  if (error) {
    console.warn("[organizer-sync] loadPendingInvitesForUser error", error);
    return [];
  }

  return (data ?? []).map((row) => ({
    ...(row as CollaboratorRow),
    event_name: (row.organizer_events as { event_name: string })?.event_name ?? "Unknown event",
  }));
};

export const acceptCollaboratorInvite = async (
  inviteId: string,
  userId: string
): Promise<{ error: string | null }> => {
  const { error } = await supabase
    .from("event_collaborators")
    .update({ accepted_at: new Date().toISOString(), user_id: userId })
    .eq("id", inviteId);

  if (error) {
    return { error: error.message };
  }

  return { error: null };
};

export const claimPendingCollaboratorInvitesForUser = async (
  userId: string,
  userEmail: string,
  eventId?: string,
): Promise<{ acceptedInvites: CollaboratorInviteRow[]; directCaptainEventId: string | null }> => {
  const pendingInvites = await loadPendingInvitesForUser(userEmail);
  const relevantInvites = eventId
    ? pendingInvites.filter((invite) => invite.event_id === eventId)
    : pendingInvites;

  const acceptedInvites: CollaboratorInviteRow[] = [];
  for (const invite of relevantInvites) {
    const { error } = await acceptCollaboratorInvite(invite.id, userId);
    if (error) {
      console.warn("[organizer-sync] claimPendingCollaboratorInvitesForUser error", invite.id, error);
      continue;
    }
    acceptedInvites.push(invite);
  }

  const directCaptainEventId =
    acceptedInvites.length === 1 &&
    acceptedInvites[0].role === "captain" &&
    acceptedInvites[0].community_side
      ? acceptedInvites[0].event_id
      : null;

  return { acceptedInvites, directCaptainEventId };
};

export const declineCollaboratorInvite = async (
  inviteId: string
): Promise<void> => {
  await supabase.from("event_collaborators").delete().eq("id", inviteId);
};

export type EventCreatorInfo = { creatorDisplayName: string; isOwn: boolean };

export const loadEventCreators = async (): Promise<Record<string, EventCreatorInfo>> => {
  const { data, error } = await supabase.rpc("get_event_creator_emails");
  if (error) {
    console.warn("[organizer-sync] loadEventCreators error", error);
    return {};
  }
  return Object.fromEntries(
    (data ?? []).map((row: { event_id: string; creator_email: string; is_own: boolean }) => [
      row.event_id,
      {
        creatorDisplayName: row.creator_email?.split("@")[0] || row.creator_email || "Unknown",
        isOwn: row.is_own,
      },
    ])
  );
};
